import { useGetUserSubscription } from '@forwardflow/hooks/customHooks'
import { usePurchaseCreditModalStore } from '@forwardflow/store/modal'
import { Modal, message } from 'antd'
import { useState } from 'react'
import SubscriptionService from '@forwardflow/services/SubscriptionService'

const PRESET_AMOUNTS = [50, 100, 200, 500]

export const PurchaseCreditModal = () => {
   const { data, mutate } = useGetUserSubscription()
   const { isPurchaseCreditModalOpen, closePurchaseCreditModal } =
      usePurchaseCreditModalStore()

   const [amount, setAmount] = useState<string>('100')
   const [isLoading, setIsLoading] = useState(false)

   const numericAmount = Number(amount) || 0
   const card = data?.cardDetails

   const handlePurchase = async () => {
      if (numericAmount <= 0) return
      setIsLoading(true)
      try {
         await SubscriptionService.purchaseCredits(numericAmount)
         message.success(`$${numericAmount.toFixed(2)} credits added`)
         await mutate()
         closePurchaseCreditModal()
         setAmount('100')
      } catch {
         message.error('Failed to purchase credits')
      } finally {
         setIsLoading(false)
      }
   }

   return (
      <Modal
         className="ff-modal"
         title=""
         open={isPurchaseCreditModalOpen}
         onCancel={closePurchaseCreditModal}
         footer={null}
         width={380}
      >
         <div className="flex flex-col gap-4 py-5 px-6">
            <p className="text-white font-semibold text-base">Add Credits</p>

            {/* Amount */}
            <div className="relative">
               <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
               <input
                  type="number"
                  min={1}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-7 pr-3 py-2.5 text-white font-medium focus:outline-none focus:border-white/20 transition-colors"
                  placeholder="100"
               />
            </div>

            {/* Presets */}
            <div className="flex gap-2">
               {PRESET_AMOUNTS.map((preset) => (
                  <button
                     key={preset}
                     onClick={() => setAmount(String(preset))}
                     className={`flex-1 py-1.5 text-xs rounded-md transition-colors ${
                        numericAmount === preset
                           ? 'bg-white/10 text-white'
                           : 'bg-white/[0.03] text-white/40 hover:bg-white/[0.06] hover:text-white/60'
                     }`}
                  >
                     ${preset}
                  </button>
               ))}
            </div>

            {/* Card */}
            {card && (
               <p className="text-white/30 text-xs">
                  Charged to {card.brand} {card.cardNumber}
               </p>
            )}

            {/* Actions */}
            <div className="flex gap-3">
               <button
                  onClick={closePurchaseCreditModal}
                  disabled={isLoading}
                  className="flex-1 py-2 text-sm text-white/60 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 rounded-lg transition-colors disabled:opacity-50"
               >
                  Cancel
               </button>
               <button
                  onClick={handlePurchase}
                  disabled={isLoading || numericAmount <= 0}
                  className="flex-1 py-2 text-sm text-white bg-primary hover:bg-primary/80 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
               >
                  {isLoading ? 'Processing...' : `Add $${numericAmount > 0 ? numericAmount : 0}`}
               </button>
            </div>
         </div>
      </Modal>
   )
}
